import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { StaffRole } from '@bisa/shared';

export interface StaffProfile {
  id: string;
  facilityId: string;
  facilityName: string;
  role: StaffRole;
}

export type UserKind = 'super_admin' | 'staff';

interface AuthState {
  session: Session | null;
  staff: StaffProfile | null;
  kind: UserKind | null;
  loading: boolean;
  error: string | null;
  init: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

async function resolveProfile(session: Session): Promise<{ staff: StaffProfile | null; kind: UserKind }> {
  const meta = session.user.app_metadata as Record<string, unknown>;
  if (meta?.role === 'super_admin') {
    return { staff: null, kind: 'super_admin' };
  }

  const { data } = await supabase
    .from('staff_members')
    .select('id, facility_id, role, facilities(name)')
    .eq('user_id', session.user.id)
    .single();

  if (!data) return { staff: null, kind: 'staff' };

  const fac = data.facilities as unknown as { name: string } | null;
  return {
    staff: {
      id: data.id,
      facilityId: data.facility_id,
      facilityName: fac?.name ?? '',
      role: data.role as StaffRole,
    },
    kind: 'staff',
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  staff: null,
  kind: null,
  loading: true,
  error: null,

  init: async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { staff, kind } = await resolveProfile(session);
      set({ session, staff, kind, loading: false });
    } else {
      set({ loading: false });
    }

    supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) {
        const { staff, kind } = await resolveProfile(session);
        set({ session, staff, kind });
      } else {
        set({ session: null, staff: null, kind: null });
      }
    });
  },

  signIn: async (email, password) => {
    set({ loading: true, error: null });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error || !data.session) {
      set({ loading: false, error: error?.message ?? 'Sign-in failed' });
      return;
    }
    const { staff, kind } = await resolveProfile(data.session);
    set({ session: data.session, staff, kind, loading: false });
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ session: null, staff: null, kind: null });
  },
}));
